import React, { useState } from "react";
import { Box, IconButton, Menu, MenuItem, Switch } from "@mui/material";
import {
  AccountCircle,
  Logout,
  BugReport,
  Keyboard,
  InfoOutlined,
  Login,
  TimerOff,
} from "@mui/icons-material";
import { MobiliAvatar } from "../../atoms/MobiliAvatar";
import useStore from "../../helpers/useStore";
import { AuthContext } from "../../stores/Auth/auth.provider";
import { UserContext } from "../../stores/User/User.provider";
import useDebugUI from "../../hooks/useDebugUI";
import { observer } from "mobx-react";
import { HotkeysHelp } from "../../hotkeys/HotkeysHelp";
import { AboutDialog } from "./AboutDialog";
import { PunchClockDialog, isPunchedInToday } from "./PunchClockDialog";
import { useNavigate } from "react-router-dom";

interface UserProfileMenuProps {
  style?: React.CSSProperties;
}

const UserProfileMenu = observer(({ style }: UserProfileMenuProps) => {
  const userStore = useStore(UserContext);
  const { user } = userStore;
  const authStore = useStore(AuthContext);
  const navigate = useNavigate();
  const { debugUI, toggleDebugUI } = useDebugUI();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState<null | HTMLElement>(null);
  const [hotkeysHelpOpen, setHotkeysHelpOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [punchAction, setPunchAction] = useState<"in" | "out" | null>(null);

  const canPunch = userStore.hasPermission("punchIn");
  const punchedIn = isPunchedInToday(user);

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const handleLogout = async () => {
    await authStore.logout();
    handleMenuClose();
    navigate("/login", { replace: true });
  };

  const handleHotkeysHelp = () => {
    setHotkeysHelpOpen(true);
    handleMenuClose();
  };

  const handleAbout = () => {
    setAboutOpen(true);
    handleMenuClose();
  };

  const handlePunch = () => {
    setPunchAction(punchedIn ? "out" : "in");
    handleMenuClose();
  };

  const menuId = "primary-search-account-menu";
  const mobileMenuId = "primary-search-account-menu-mobile";

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleMenuClose}>
        <AccountCircle />
        Profile
      </MenuItem>
      {canPunch && (
        <MenuItem onClick={handlePunch}>
          {punchedIn ? <TimerOff /> : <Login />}
          {punchedIn ? "Ausstempeln" : "Einstempeln"}
        </MenuItem>
      )}
      {/* No <label> around the Switch: a label forwards a second click to its
          input, which would bubble here and toggle twice (i.e. never). The
          MenuItem is the only click target; the Switch is display-only. */}
      <MenuItem
        onClick={() => {
          toggleDebugUI();
          handleMenuClose(); // Close the menu after toggling
        }}
      >
        <BugReport />
        Debug UI
        <Switch
          checked={debugUI}
          readOnly
          tabIndex={-1}
          size="small"
          sx={{ ml: "auto", pointerEvents: "none" }}
        />
      </MenuItem>
      <MenuItem onClick={handleHotkeysHelp}>
        <Keyboard />
        Tastaturkürzel
      </MenuItem>
      <MenuItem onClick={handleAbout}>
        <InfoOutlined />
        Über
      </MenuItem>
      <MenuItem onClick={handleLogout}>
        <Logout />
        Logout
      </MenuItem>
    </Menu>
  );

  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem onClick={handleProfileMenuOpen}></MenuItem>
    </Menu>
  );

  if (!user) {
    return null;
  }

  return (
    <>
      {/* Desktop Avatar */}
      <Box sx={{ display: { xs: "none", md: "flex" }, ...style }}>
        <MobiliAvatar user={user} onClick={handleProfileMenuOpen} sx={{ cursor: "pointer" }} />
      </Box>

      {/* Mobile Menu Button */}
      <Box sx={{ display: { xs: "flex", md: "none" } }}>
        <IconButton
          size="large"
          aria-label="show more"
          aria-controls={mobileMenuId}
          aria-haspopup="true"
          onClick={handleMobileMenuOpen}
          color="info"
        >
          <AccountCircle style={{ color: "blue" }} />
        </IconButton>
      </Box>

      {/* Desktop Menu */}
      {renderMenu}

      {/* Mobile Menu */}
      {renderMobileMenu}

      {/* Hotkeys Help Dialog */}
      <HotkeysHelp open={hotkeysHelpOpen} onClose={() => setHotkeysHelpOpen(false)} />

      {/* About Dialog */}
      <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />

      {/* Punch Clock Dialog */}
      {punchAction && (
        <PunchClockDialog open action={punchAction} onClose={() => setPunchAction(null)} />
      )}
    </>
  );
});

export default UserProfileMenu;
